import { useEffect, useState } from "react";
import { Bell, CalendarCheck, ChartColumn, HeartPulse, Home, LogOut, PanelLeftClose, PanelLeftOpen, Sparkles, UserRound } from "lucide-react";
import { NavLink } from "react-router-dom";

import ivoryHealthLogo from "@/assets/ivory-health-logo.webp";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { getTenantLogo } from "@/lib/tenantLogos";

const COLLAPSE_KEY = "ivory.navCollapsed";

// The four places people go to do something.
const primary = [
  { to: "/", label: "Home", Icon: Home, testId: "bottom-nav-home-link" },
  { to: "/health-checks", label: "Checks", Icon: CalendarCheck, testId: "bottom-nav-checks-link" },
  { to: "/consultations", label: "Consult", Icon: HeartPulse, testId: "bottom-nav-consult-link" },
  { to: "/offers", label: "Offers", Icon: Sparkles, testId: "bottom-nav-offers-link" },
];

// Account rather than activity. On a phone these stay in the header, where
// there is no room for eight targets along the bottom.
const secondary = [
  { to: "/reports", label: "Reports", Icon: CalendarCheck, testId: "side-nav-reports-link" },
  { to: "/updates", label: "Notifications", Icon: Bell, testId: "side-nav-updates-link" },
  { to: "/profile", label: "Profile", Icon: UserRound, testId: "side-nav-profile-link" },
];

// Only for the handful of people at each client who have the HR flag, so it is
// appended at render time rather than living in the list above.
const hrOnly = { to: "/insights", label: "Insights", Icon: ChartColumn, testId: "side-nav-insights-link" };

/**
 * One nav, two shapes.
 *
 * On a phone it is the bar along the bottom, carrying only the four primary
 * destinations. From 900px it is a sidebar carrying the brand, everything
 * else, and sign out — so the account controls live with the navigation
 * instead of being scattered along the top.
 *
 * It collapses to icons, which is remembered between visits.
 */
export function BottomNavigation() {
  const { signOut, user } = useAuth();
  const { name: tenantName, client_id: clientId, logo_url: logoUrl } = useTenant();
  const tenantLogo = getTenantLogo(clientId) || logoUrl;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      /* a remembered preference, nothing depends on it */
    }
  }, [collapsed]);

  const renderLink = ({ to, label, Icon, testId }) => (
    <NavLink key={to} to={to} end={to === "/"} className="bottom-navigation__link" title={label} data-testid={testId}>
      <Icon aria-hidden="true" size={20} strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <nav
      className={`bottom-navigation ${collapsed ? "is-collapsed" : ""}`}
      aria-label="Main navigation"
      data-testid="bottom-navigation"
    >
      <div className="side-nav__brand">
        {tenantLogo ? (
          <img src={tenantLogo} alt={tenantName} data-testid="side-nav-logo" />
        ) : (
          <span className="brand-lockup__mark">{tenantName.charAt(0)}</span>
        )}
        <div>
          <strong>{tenantName}</strong>
          <span>Health Hub</span>
        </div>
      </div>

      <div className="side-nav__group">{primary.map(renderLink)}</div>

      <div className="side-nav__group side-nav__group--secondary">
        <p className="side-nav__heading">Account</p>
        {(user?.is_hr ? [...secondary, hrOnly] : secondary).map(renderLink)}
        <button type="button" className="bottom-navigation__link" onClick={signOut} title="Sign out" data-testid="side-nav-sign-out">
          <LogOut aria-hidden="true" size={20} strokeWidth={2} />
          <span>Sign out</span>
        </button>
      </div>

      <button
        type="button"
        className="side-nav__collapse"
        onClick={() => setCollapsed((current) => !current)}
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        aria-pressed={collapsed}
        data-testid="side-nav-collapse"
      >
        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        <span>Collapse</span>
      </button>
    </nav>
  );
}
